import { FC, useEffect, useState } from 'react'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import cn from './index.module.css'
import { useGetExtraPurchaseFields } from '@pages/Admin/PlatformSettings2/DirectorySettings/components/ExtraPurchaseFields/utils'
import EmailOrPhoneField from '../EmailOrPhoneField/EmailOrPhoneField'
import { Radio } from 'antd'
import {
  ICheckEmailOrPhoneResponse,
  IEnrollmentProfileData,
} from '../../utils/api'
import { sortingArray } from '@/helpers/sortingArray'
import { useGetSettings } from '@pages/CoursesCatalog/tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import { jsonConvert } from '@/helpers/jsonConvert'
import { checkIsEmail, checkIsPhone } from '../../utils/validation'
import classNames from 'classnames'
import ParentEmailOrPhoneField from '../ParentEmailOrPhoneField/ParentEmailOrPhoneField'

interface IExtraPurchaseFieldsView {
  courseId?: number
  classId?: number
  userId?: number
  profile: IEnrollmentProfileData
  updateProfile: (field: keyof IEnrollmentProfileData, value: string) => void
  token: string
  code: string
  setCode: (value: string) => void
  userExistedData: ICheckEmailOrPhoneResponse
  setUserExistedData: (value: ICheckEmailOrPhoneResponse) => void
  parentExistedData: ICheckEmailOrPhoneResponse
  setParentExistedData: (value: ICheckEmailOrPhoneResponse) => void
  codeDeliveryMethod: string
  setCodeDeliveryMethod: (value: string) => void
  validationErrors: string[]
  setValidationErrors: React.Dispatch<
    React.SetStateAction<(keyof IEnrollmentProfileData)[]>
  >
}

const FIELD_LABELS: Record<string, string> = {
  studentName: 'ФИО',
  studentEmail: 'Почта',
  studentPhone: 'Телефон',
  password: 'Придумайте пароль',
  parentName: 'ФИО родителя', 
  parentPhone: 'Телефон родителя',
  parentEmail: 'Почта родителя',
  parentPassword: 'Придумайте пароль родителя',
  customText: 'Дополнительная информация',
}

interface ConfirmationMethod {
  key: string
  label: string
  order: number
}

const ExtraPurchaseFieldsView: FC<IExtraPurchaseFieldsView> = ({
  classId,
  courseId,
  userId,
  profile,
  updateProfile,
  token,
  code,
  setCode,
  userExistedData,
  setUserExistedData,
  parentExistedData,
  setParentExistedData,
  codeDeliveryMethod,
  setCodeDeliveryMethod,
  validationErrors,
  setValidationErrors,
}) => {
  const { checkSettings, getSettings, loadingSettings } = useGetSettings()
  const { extraFields } = useGetExtraPurchaseFields()
  const sms_integration_enabled = checkSettings('sms_integration_enabled') // активна ли интеграция с смс
  const registrationTypeForCatalog = sms_integration_enabled
    ? getSettings('registrationTypeForCatalog') ?? 'email'
    : extraFields?.includes('studentEmail')
    ? 'email'
    : 'phone' // тип регистрации

  const requiredNumberConfirmationForCatalog = checkSettings(
    'requiredNumberConfirmationForCatalog',
  ) // обязательно ли подтверждать телефон

  const confirmationByPhoneNumber = checkSettings('confirmationByPhoneNumber') // есть вариант подтвердить по смс на телефон
  const confirmationByTelegram = checkSettings('confirmationByTelegram') // есть вариант подтвердить по телеграмм
  const ordersForConfirmation = !!getSettings('ordersForConfirmation')?.length
    ? jsonConvert(getSettings('ordersForConfirmation'))
    : []
  // порядок полей телеграмм и телефон
  const [confirmationCheckboxes, setConfirmationCheckboxes] = useState<
    ConfirmationMethod[]
  >([]) // сюда поместим с правильным

  // порядком чекбоксы выбора метода пользователем
  useEffect(() => {
    if (loadingSettings) return
    const newData = sortingArray(
      [
        ...(confirmationByPhoneNumber
          ? [
              {
                key: 'sms',
                label: 'Смс',
                order: ordersForConfirmation?.findIndex(
                  (el: string) => el == 'confirmationByPhoneNumber',
                ),
              },
            ]
          : []),
        ...(confirmationByTelegram
          ? [
              {
                key: 'telegram',
                label: 'Telegram',
                order: ordersForConfirmation?.findIndex(
                  (el: string) => el == 'confirmationByTelegram',
                ),
              },
            ]
          : []),
      ],
      'order',
    )

    if (!newData?.length) return

    setCodeDeliveryMethod(newData?.[0]?.key) // по умолчанию ставим первый видимый
    setConfirmationCheckboxes(newData)
  }, [loadingSettings])

  const smsIntegrationEnabled =
    sms_integration_enabled &&
    requiredNumberConfirmationForCatalog &&
    (confirmationByPhoneNumber || confirmationByTelegram) &&
    registrationTypeForCatalog === 'phone' &&
    !userExistedData.user
  // нужно ли подтверждать номер исходя их всех условий

  const getToken = !token && smsIntegrationEnabled // нужно ли получать токен

  const showAdditionalField = !userExistedData?.user
  const showParentAdditionalField = !parentExistedData?.user

  const handleChange = (key: keyof IEnrollmentProfileData, value: string) => {
    updateProfile(key, value)
  }

  const updateEmail = (value: string) => {
    updateProfile('studentEmail', value)
    if (checkIsEmail(value)) {
      setValidationErrors((prev) => [
        ...prev?.filter((item) => item !== 'studentEmail'),
      ])
    } else {
      setValidationErrors((prev) =>
        Array.from(new Set([...prev, 'studentEmail'])),
      )
    }
  }

  const updatePhone = (value: string) => {
    updateProfile('studentPhone', value)
    if (checkIsPhone(value)) {
      setValidationErrors((prev) => [
        ...prev?.filter((item) => item !== 'studentPhone'),
      ])
    } else {
      setValidationErrors((prev) =>
        Array.from(new Set([...prev, 'studentPhone'])),
      )
    }
  }

  const updatePhoneParent = (value: string) => {
    updateProfile('parentPhone', value)
    if (checkIsPhone(value)) {
      setValidationErrors((prev) => [
        ...prev?.filter((item) => item !== 'parentPhone'),
      ])
    } else {
      setValidationErrors((prev) =>
        Array.from(new Set([...prev, 'parentPhone'])),
      )
    }
  }

  const updateEmailParent = (value: string) => {
    updateProfile('parentEmail', value)
    if (checkIsEmail(value)) {
      setValidationErrors((prev) => [
        ...prev?.filter((item) => item !== 'parentEmail'),
      ])
    } else {
      setValidationErrors((prev) =>
        Array.from(new Set([...prev, 'parentEmail'])),
      )
    }
  }

  useEffect(() => {
    if (
      profile.studentEmail &&
      registrationTypeForCatalog === 'email' &&
      !checkIsEmail(profile.studentEmail)
    ) {
      setValidationErrors((prev) => [...prev, 'studentEmail'])
    }
    if (
      profile.studentPhone &&
      registrationTypeForCatalog === 'phone' &&
      !checkIsPhone(profile.studentPhone)
    ) {
      setValidationErrors((prev) => [...prev, 'studentPhone'])
    }
  }, [profile.studentEmail, profile.studentPhone])

  return (
    <div className={cn.list}>
      {extraFields
        .filter((fieldKey) => userId ? !['studentName', 'studentPhone', 'studentEmail', 'password'].includes(fieldKey) : true) // исключаем поля студента, если передан user_id из crm
        .map((fieldKey) => {
        if (['studentPhone', 'studentEmail']?.includes(fieldKey)) {
          return (
            <>
              <EmailOrPhoneField
                key={fieldKey + 'field'}
                classId={classId}
                courseId={courseId}
                value={
                  registrationTypeForCatalog === 'phone'
                    ? profile.studentPhone
                    : profile.studentEmail
                }
                type={
                  registrationTypeForCatalog === 'email' ? 'email' : 'phone'
                }
                onChange={
                  registrationTypeForCatalog === 'phone'
                    ? updatePhone
                    : updateEmail
                }
                isInvalid={
                  registrationTypeForCatalog === 'phone'
                    ? validationErrors?.includes('studentPhone')
                    : validationErrors?.includes('studentEmail')
                }
                onExistedChange={setUserExistedData}
                existedData={userExistedData}
                byConfirmationCode={smsIntegrationEnabled}
              />
              {!!token && (
                <TextField
                  key={fieldKey + 'token'}
                  className={classNames(cn.inputField, cn.inputFieldKode)}
                  placeholder="Код"
                  value={code}
                  onChange={setCode}
                />
              )}

              {getToken && (
                <div
                  className={cn.radioGroupWrapper}
                  key={fieldKey + 'confirm'}
                >
                  <span className={classNames(cn.text, cn.textRed)}>
                    {confirmationCheckboxes?.length > 1 &&
                      'Выберите, куда отправить вам временный код для подтверждения'}
                    {confirmationCheckboxes?.length == 1 &&
                      confirmationByPhoneNumber &&
                      'Временный код для подтверждения будет отправлен по смс'}
                    {confirmationCheckboxes?.length == 1 &&
                      confirmationByTelegram &&
                      'Временный код для подтверждения будет отправлен в телеграм'}
                  </span>
                  {confirmationCheckboxes?.length > 1 && (
                    <Radio.Group
                      value={codeDeliveryMethod}
                      onChange={(e) => setCodeDeliveryMethod(e.target.value)}
                    >
                      {confirmationCheckboxes.map((el) => {
                        return <Radio value={el.key}>{el.label}</Radio>
                      })}
                    </Radio.Group>
                  )}
                </div>
              )}
            </>
          )
        }

        if (!showAdditionalField && !['parentPassword', 'parentName', 'parentEmail', 'parentPhone']?.includes(fieldKey)) return <></>

        if (
          !showParentAdditionalField &&
          ['parentPassword', 'parentName']?.includes(fieldKey)
        )
          return <></>

        if (['parentPhone', 'parentEmail']?.includes(fieldKey)) {
          return (
            <ParentEmailOrPhoneField
              key={fieldKey + 'field'}
              classId={classId}
              courseId={courseId}
              value={
                fieldKey === 'parentPhone'
                  ? profile.parentPhone
                  : profile.parentEmail
              }
              type={fieldKey === 'parentEmail' ? 'email' : 'phone'}
              onChange={
                fieldKey === 'parentPhone' ? updatePhoneParent : updateEmailParent
              }
              isInvalid={
                fieldKey === 'parentPhone'
                  ? validationErrors?.includes('parentPhone')
                  : validationErrors?.includes('parentEmail')
              }
              onExistedChange={setParentExistedData}
              existedData={parentExistedData}
            />
          )
        }

        return (
          <TextField
            key={fieldKey}
            placeholder={FIELD_LABELS[fieldKey] ?? fieldKey}
            className={cn.inputField}
            value={profile?.[fieldKey as keyof IEnrollmentProfileData] ?? ''}
            onChange={(val) =>
              handleChange(fieldKey as keyof IEnrollmentProfileData, val)
            }
            isInvalid={validationErrors.includes(
              fieldKey as keyof IEnrollmentProfileData,
            )}
          />
        )
      })}
    </div>
  )
}

export default ExtraPurchaseFieldsView
